import { useCallback } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Panel,
  useReactFlow,
  type Node,
  type Edge,
  type Viewport,
  type Connection,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

import { useWorkflowStore } from '@/stores/workflow-store'
import { Button } from '@/components/ui/button'
import { nodeTypes } from './nodes'
import { edgeTypes } from './edges'
import { NodePalette, isValidNodeType } from './node-palette'
import { useElkLayout } from './use-elk-layout'
import type { NodeType } from '@/schemas/workflow-node'

interface FlowCanvasInnerProps {
  readOnly?: boolean
}

function FlowCanvasInner({ readOnly = false }: FlowCanvasInnerProps) {
  const { t } = useTranslation('workflows')
  const {
    flowNodes,
    flowEdges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    selectNode,
    selectEdge,
    flowViewport,
    setFlowViewport,
  } = useWorkflowStore()
  const { applyLayout, isLayouting } = useElkLayout()
  const reactFlowInstance = useReactFlow()

  // Handle drop from palette — T-02-03 mitigation: validate type before addNode
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      const type = event.dataTransfer.getData('application/reactflow')
      if (!type || !isValidNodeType(type)) return
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })
      addNode(type as NodeType, position)
    },
    [reactFlowInstance, addNode]
  )

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  // Handle connection with cycle detection feedback
  const handleConnect = useCallback(
    (connection: Connection) => {
      const prevEdgeCount = useWorkflowStore.getState().flowEdges.length
      onConnect(connection)
      // If edge count didn't change, cycle was detected
      setTimeout(() => {
        const currentEdges = useWorkflowStore.getState().flowEdges
        if (currentEdges.length === prevEdgeCount) {
          toast.error(
            t('flow.graphCycle', {
              a: connection.source,
              b: connection.target,
            })
          )
        }
      }, 0)
    },
    [onConnect, t]
  )

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      selectNode(node.id)
    },
    [selectNode]
  )

  const onEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      selectEdge(edge.id)
    },
    [selectEdge]
  )

  const onPaneClick = useCallback(() => {
    selectNode(null)
    selectEdge(null)
  }, [selectNode, selectEdge])

  const onMoveEnd = useCallback(
    (_: unknown, viewport: Viewport) => {
      setFlowViewport(viewport)
    },
    [setFlowViewport]
  )

  const isEmpty = flowNodes.length === 0

  // EmptyFlowState CTA: add LLM node then fitView to center it
  const handleAddFirstLLM = useCallback(() => {
    addNode('llm', { x: 250, y: 200 })
    // Allow React Flow to process the new node before fitting
    setTimeout(() => {
      reactFlowInstance.fitView({ duration: 300 })
    }, 50)
  }, [addNode, reactFlowInstance])

  return (
    <div className="h-[calc(100vh-240px)] w-full rounded-xl border border-border relative">
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={readOnly ? undefined : handleConnect}
        onDrop={readOnly ? undefined : onDrop}
        onDragOver={readOnly ? undefined : onDragOver}
        onNodeClick={readOnly ? undefined : onNodeClick}
        onEdgeClick={readOnly ? undefined : onEdgeClick}
        onPaneClick={readOnly ? undefined : onPaneClick}
        onMoveEnd={readOnly ? undefined : onMoveEnd}
        defaultViewport={flowViewport}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={{ type: 'conditional' }}
        fitView={isEmpty || readOnly}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
        deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
        className="bg-background"
      >
        <Background color="rgba(99, 102, 241, 0.15)" gap={20} size={1.2} />
        <Controls
          showInteractive={false}
          className="archon-flow-controls"
        />
        <MiniMap
          nodeColor={(node) => {
            const accents: Record<string, string> = {
              start: 'rgb(52, 211, 153)',
              end: 'rgb(251, 113, 133)',
              llm: 'rgb(129, 140, 248)',
              code: 'rgb(52, 211, 153)',
              http: 'rgb(251, 191, 36)',
              agent: 'rgb(192, 132, 252)',
              router: 'rgb(251, 191, 36)',
              join: 'rgb(56, 189, 248)',
              task: 'rgb(129, 140, 248)',
            }
            return accents[node.type ?? 'task'] ?? 'rgb(129, 140, 248)'
          }}
          nodeStrokeWidth={2}
          pannable
          zoomable
          className="archon-flow-minimap"
        />
        {!readOnly && (
          <Panel position="top-left">
            <NodePalette />
          </Panel>
        )}
        {!readOnly && (
          <Panel position="top-right">
            <Button
              variant="outline"
              size="sm"
              onClick={applyLayout}
              disabled={isLayouting || isEmpty}
            >
              {isLayouting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : null}
              {t('flow.autoLayout')}
            </Button>
          </Panel>
        )}
      </ReactFlow>
      {isEmpty && !readOnly && <EmptyFlowState onAddLLM={handleAddFirstLLM} />}
    </div>
  )
}

/** Empty state overlay shown when the canvas has zero nodes (UI-SPEC). */
function EmptyFlowState({ onAddLLM }: { onAddLLM: () => void }) {
  const { t } = useTranslation('workflows')
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
      <div className="text-center space-y-4 p-12 pointer-events-auto">
        <h2 className="text-[28px] font-semibold text-foreground">
          {t('flow.emptyStateHeading')}
        </h2>
        <p className="text-base text-muted-foreground max-w-md">
          {t('flow.emptyStateBody')}
        </p>
        <Button variant="default" onClick={onAddLLM}>
          {t('flow.emptyStateCta')}
        </Button>
      </div>
    </div>
  )
}

interface FlowCanvasProps {
  /**
   * Render in read-only mode: disables drag/connect/select, suppresses
   * store-driven handlers, and hides the node palette + auto-layout
   * button. Used by the Phase 03 diff drawer.
   */
  readOnly?: boolean
}

/** Flow canvas wrapped in ReactFlowProvider. */
export function FlowCanvas({ readOnly }: FlowCanvasProps = {}) {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner readOnly={readOnly} />
    </ReactFlowProvider>
  )
}
